import { useState } from 'react';
import { App, Button, Card, Tag, Typography } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { QualificationFlow, QualifyingFlowQuestion, salesChannelLabel } from '../../../../dto/qualifying-flow-models';
import { QualificationFlowForm } from './qualifying-flow-form';

const { Title, Text } = Typography;

type Props = {
  flow: QualificationFlow;
  allQuestions: QualifyingFlowQuestion[];
  onSubmit: (flow: QualificationFlow) => void;
  onDelete: (id: number) => void;
  onDuplicate: (flow: QualificationFlow) => void;
  shouldDisplayAddButton: (status: boolean) => void;
  loading?: boolean;
};

export const QualificationFlowCard = ({ flow, allQuestions, onSubmit, onDelete, onDuplicate, shouldDisplayAddButton, loading }: Props) => {
  const { modal } = App.useApp();
  const [shouldEdit, setShouldEdit] = useState(false);

  const setEditDisplay = (status: boolean) => {
    shouldDisplayAddButton(!status);
    setShouldEdit(status);
  };

  const handleDuplicate = () => {
    const duplicated: QualificationFlow = {
      ...flow,
      id: 0,
      feId: crypto.randomUUID(),
      name: `${flow.name} COPY`,
      isActive: false,
      questions: flow.questions.map((q) => ({ ...q, feId: crypto.randomUUID() })),
    };
    onDuplicate(duplicated);
  };

  const showDeleteConfirm = () => {
    modal.confirm({
      title: 'Are you sure you want to delete this flow?',
      icon: <DeleteOutlined />,
      content: 'This will remove the flow and its question assignments. Questions will not be affected.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        onDelete(flow.id);
      },
    });
  };

  const onSubmitEdit = (updated: QualificationFlow) => {
    onSubmit(updated);
    setShouldEdit(false);
    shouldDisplayAddButton(true);
  };

  return (
    <Card style={{ marginBottom: '25px' }}>
      {shouldEdit ? (
        <QualificationFlowForm
          flow={flow}
          allQuestions={allQuestions}
          onSubmit={onSubmitEdit}
          onCancel={() => setEditDisplay(false)}
          loading={loading}
        />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {flow.name}
              </Title>
              <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                <Tag color="blue">{salesChannelLabel[flow.salesChannel] ?? flow.salesChannel}</Tag>
                <Tag color={flow.isActive ? 'green' : 'default'}>{flow.isActive ? 'Active' : 'Inactive'}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {flow.questions.length} question{flow.questions.length !== 1 ? 's' : ''}
                </Text>
              </div>
            </div>
            <div style={{ flex: '0 0 auto', whiteSpace: 'nowrap', marginLeft: 16 }}>
              <Button onClick={() => setEditDisplay(true)} icon={<EditOutlined />} style={{ marginRight: '10px' }} disabled={loading}>
                Edit
              </Button>
              <Button onClick={handleDuplicate} icon={<CopyOutlined />} style={{ marginRight: '10px' }} disabled={loading}>
                Duplicate
              </Button>
              <Button onClick={showDeleteConfirm} icon={<DeleteOutlined />} loading={loading} disabled={loading}>
                Delete
              </Button>
            </div>
          </div>
          {flow.description && <p style={{ marginTop: 8, marginBottom: 0 }}>{flow.description}</p>}
        </>
      )}
    </Card>
  );
};
